import React, { useEffect, useRef, useState } from 'react';
import { Info, Loader2, X } from 'lucide-react';
import { Link, NavLink, useLocation, useSearchParams } from 'react-router-dom';
import PolygonToolbar from '../../components/PolygonToolbar';
import CanvasRenderer from '../../components/CanvasRenderer';
import CameraSelector from '../../components/CameraSelector';
import { loadCameraData, loadPolygonData, loadZoneSummary } from '../../utils/polygonLoader';
import { useAppActions, useAppState, Frame } from '../../app/appContext';
import BranchGeneralSettings from '../../components/BranchGeneralSettings';
import BranchPlanSettings from '../../components/BranchPlanSettings';
import ChecklistsSettings from '../../components/ChecklistsSettings';
import VisitsSettings from '../../components/VisitsSettings';
import SalesPlanSettings from '../../components/SalesPlanSettings';
import ReceiptsSettings from '../../components/ReceiptsSettings';
import QueuesStaffSettings from '../../components/QueuesStaffSettings';
import ReviewsSettingsPage from '../../components/reviews/ReviewsSettings';
import { FeatureGate } from '../../components/features/FeatureGate';
import SpeechSettings from '../../components/SpeechSettings';
import ReportAutoSendCard from '../../components/ReportAutoSendCard';
import TelegramBotSettings from '../../components/TelegramBotSettings';
import EquipmentSettings from '../../components/EquipmentSettings';
import GrafanaDashboardsSettings from '../../components/GrafanaDashboardsSettings';
import ErrorBoundary from '../../components/common/ErrorBoundary';
import PolygonSidebar from '../../components/PolygonSidebar';
import DrawingStatusBlock from '../../components/DrawingStatusBlock';
import { EditToolbar } from '../../components/zoning/EditToolbar';
import { ResizableSidebar } from '../../components/zoning/ResizableSidebar';
import { ToolbarSlotProvider, useToolbarSlot } from '../../components/zoning/toolbarSlot';
import { useT } from '../../contexts/I18nContext';
import { usePageTitle } from '../../contexts/PageHeaderContext';
import { CONNECTED_FEATURES, FUNCTION_SETTINGS_PATHS, useFunctionSettingsItems } from '../../components/layout/functionSettingsNav';
import { PluginSetupPanel } from '../../components/PluginTeaserRow';
import { useCurrentZone } from '../../hooks/useCurrentZone';
import { cn } from '../../lib/utils';

type TabKey = 'features' | 'general' | 'zoning' | 'queuesStaff' | 'checklists' | 'receipts' | 'salesPlan' | 'visits' | 'reviews' | 'speech' | 'telegram' | 'equipment' | 'plan' | 'integrations';

interface TabMeta {
  key: TabKey;
  path: string;
  titleKey: string;
  descriptionKey?: string;
}

const TABS: readonly TabMeta[] = [
  { key: 'features',    path: '/management/features',    titleKey: 'sidebar.connectedFeatures' },
  { key: 'general',     path: '/management/general',     titleKey: 'settings.general',     descriptionKey: 'settings.generalDescription' },
  { key: 'zoning',      path: '/management/zoning',      titleKey: 'settings.zoning',      descriptionKey: 'settings.zoningDescription' },
  { key: 'queuesStaff', path: '/management/queues-staff', titleKey: 'settings.queuesStaff', descriptionKey: 'settings.queuesStaffDescription' },
  { key: 'plan',        path: '/management/plan',        titleKey: 'settings.plan',        descriptionKey: 'settings.planDescription' },
  { key: 'checklists',  path: '/management/checklists',  titleKey: 'settings.checklists',  descriptionKey: 'settings.checklistsDescription' },
  // «Интеграции» (Алексей 04.11 #4035/#4036): подключение ОФД — не функция и не оборудование.
  { key: 'receipts',    path: '/management/receipts',    titleKey: 'sidebar.branchIntegrations', descriptionKey: 'features.receipts.settingsDescription' },
  { key: 'salesPlan',   path: '/management/sales-plan',  titleKey: 'features.sales_plan.name', descriptionKey: 'features.sales_plan.settingsDescription' },
  { key: 'visits',      path: '/management/visits',      titleKey: 'settings.visits',      descriptionKey: 'settings.visitsDescription' },
  { key: 'reviews',     path: '/management/reviews',     titleKey: 'features.reviews.name', descriptionKey: 'features.reviews.settingsDescription' },
  { key: 'speech',      path: '/management/speech',      titleKey: 'features.speech.name',  descriptionKey: 'features.speech.settingsDescription' },
  { key: 'telegram',    path: '/management/telegram',    titleKey: 'settings.telegram',    descriptionKey: 'settings.telegramDescription' },
  { key: 'equipment',   path: '/management/equipment',   titleKey: 'settings.equipment',   descriptionKey: 'settings.equipmentDescription' },
  { key: 'integrations', path: '/management/integrations', titleKey: 'settings.integrations', descriptionKey: 'settings.integrationsDescription' },
] as const;

function resolveTab(pathname: string): TabMeta | null {
  return TABS.find(t => pathname.startsWith(t.path)) ?? null;
}

const BranchManagement: React.FC = () => {
  const location = useLocation();
  const actions = useAppActions();
  const { camera, zone, polygons } = useAppState();
  const { t } = useT();
  const selectedCameraId = camera.selectedCameraId;
  const [loading, setLoading] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [localCameraError, setLocalCameraError] = useState<string | null>(null);

  const tab = resolveTab(location.pathname);

  const frameCacheRef = useRef<Map<string, Frame>>(new Map());

  useEffect(() => {
    if (!selectedCameraId) {
      setLocalCameraError(null);
      return;
    }
    setLoading(true);
    setLocalCameraError(null);
    const cached = frameCacheRef.current.get(selectedCameraId);
    if (cached) {
      actions.setFrame(cached);
      loadPolygonData(selectedCameraId, actions)
        .catch((error: any) => {
          console.error('Failed to load polygon data:', error);
          setLocalCameraError(t('error.loadingPolygons'));
        })
        .finally(() => setLoading(false));
    } else {
      actions.setFrameLoading(true);
      loadCameraData(selectedCameraId, actions, { useInstant: false })
        .catch((error: any) => {
          console.error('Failed to load camera data:', error);
          setLocalCameraError(t('error.loadingFrame'));
        })
        .finally(() => {
          setLoading(false);
          actions.setFrameLoading(false);
        });
    }
  }, [selectedCameraId, actions]);

  const frame = polygons.areas.frame;
  useEffect(() => {
    if (frame && selectedCameraId) {
      frameCacheRef.current.set(selectedCameraId, frame);
    }
  }, [frame, selectedCameraId]);

  useEffect(() => {
    setLocalCameraError(null);
  }, [camera.selectedCameraId, zone.selectedZoneId]);

  useEffect(() => {
    if (zone.selectedZoneId) {
      loadZoneSummary(zone.selectedZoneId, actions).catch(console.error);
    }
  }, [zone.selectedZoneId, actions]);

  async function handleReloadFrame() {
    if (!selectedCameraId) return;
    setReloading(true);
    setLocalCameraError(null);
    actions.setFrameLoading(true);
    try {
      await loadCameraData(selectedCameraId, actions, { useInstant: true });
    } catch (error: any) {
      console.error('Failed to reload frame:', error);
      setLocalCameraError(t('error.reloadingFrame'));
    } finally {
      setReloading(false);
      actions.setFrameLoading(false);
    }
  }

  function renderTabContent(key: TabKey) {
    switch (key) {
      case 'features':
        return <PluginSetupPanel zoneId={zone.selectedZoneId} />;
      case 'general':
        return <BranchGeneralSettings />;
      case 'plan':
        return <BranchPlanSettings />;
      case 'queuesStaff':
        return <QueuesStaffSettings />;
      case 'checklists':
        // M1.3: nothing until the function is switched on for this branch.
        return (
          <FeatureGate feature="checklists" path="/management/checklists">
            <ChecklistsSettings />
          </FeatureGate>
        );
      // «Чеки» без FeatureGate: включает функцию само подключение к ОФД.
      case 'receipts':
        return <ReceiptsSettings />;
      case 'salesPlan':
        return (
          <FeatureGate feature="sales_plan" path="/management/sales-plan">
            <SalesPlanSettings />
          </FeatureGate>
        );
      case 'visits':
        return (
          <FeatureGate feature="visits" path="/management/visits">
            <VisitsSettings />
          </FeatureGate>
        );
      case 'reviews':
        return <ReviewsSettingsPage />;
      case 'speech':
        return (
          <FeatureGate feature="speech" path="/management/speech">
            <SpeechSettings />
          </FeatureGate>
        );
      case 'telegram':
        return (
          <div className="space-y-4">
            <TelegramBotSettings />
            <ReportAutoSendCard />
          </div>
        );
      case 'equipment':
        return <EquipmentSettings />;
      case 'integrations':
        return <GrafanaDashboardsSettings />;
      case 'zoning':
        return (
          <ZoningTabBody
            loading={loading}
            reloading={reloading}
            onReloadFrame={handleReloadFrame}
            cameraError={localCameraError}
          />
        );
    }
  }

  if (!tab) {
    return (
      <div className="m-bleed container mx-auto max-w-5xl px-4 py-6">
        <h1 className="text-2xl font-semibold tracking-tight">404</h1>
      </div>
    );
  }

  const title = t(tab.titleKey);
  const description = tab.descriptionKey ? t(tab.descriptionKey) : null;

  // Push the page title into the header (top-right). usePageTitle clears on
  // unmount so navigating between tabs always reflects the current section.
  return <BranchManagementBody tab={tab} title={title} description={description} renderTabContent={renderTabContent} />;
};

function BranchManagementBody({
  tab,
  title,
  description,
  renderTabContent,
}: {
  tab: { key: TabKey };
  title: string;
  description: string | null;
  renderTabContent: (key: TabKey) => React.ReactNode;
}) {
  usePageTitle(title, description);
  const location = useLocation();
  const isFunction = FUNCTION_SETTINGS_PATHS.some(p => location.pathname.startsWith(p));
  const tabsBar = isFunction ? <FunctionTabs /> : null;

  if (tab.key === 'zoning') {
    // h-full + overflow-hidden: pin to main's height (main is overflow-auto for
    // other tabs but here we explicitly clamp). flex-1/min-h-0 doesn't work
    // because main isn't a flex container — its height comes from its parent's
    // flex chain, but as a non-flex parent it can't propagate flex sizing to
    // children. h-full (= height: 100%) does propagate.
    return (
      <div className="flex h-full flex-col overflow-hidden px-4 py-4 md:px-6 md:py-6">
        {tabsBar && <div className="mb-3">{tabsBar}</div>}
        {renderTabContent(tab.key)}
      </div>
    );
  }

  // Plan tab carries the auto-layout calibration UI (image + sliders side
  // by side); the default 5xl container is too tight, so give it 7xl.
  const wide = tab.key === 'plan';
  return (
    <div className={`m-bleed container mx-auto ${wide ? 'max-w-7xl' : 'max-w-5xl'} space-y-4 px-4 py-6`}>
      {tabsBar}
      <div>{renderTabContent(tab.key)}</div>
    </div>
  );
}

/** «Настройки функций» (Алексей 04.11 #4031): включённые функции вкладками, порядок как в «Данных».
 *  Каждая вкладка — своя страница (/management/<функция>), так что старые ссылки работают. */
function FunctionTabs() {
  const { t } = useT();
  const { zoneId } = useCurrentZone();
  const items = [CONNECTED_FEATURES, ...useFunctionSettingsItems(zoneId)];
  const location = useLocation();
  const zone = new URLSearchParams(location.search).get('zone');
  return (
    <nav className="-mx-1 flex gap-1 overflow-x-auto border-b border-border px-1" aria-label={t('sidebar.functionSettings')}>
      {items.map(it => {
        const Icon = it.icon;
        return (
          <NavLink key={it.to} to={{ pathname: it.to, search: zone ? `?zone=${zone}` : '' }}
            className={({ isActive }) => cn(
              '-mb-px flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium transition-colors',
              isActive ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground')}>
            <Icon className="size-4" />
            {t(it.labelKey)}
          </NavLink>
        );
      })}
    </nav>
  );
}

// ── Zoning tab ───────────────────────────────────────────────────────────────

interface ZoningTabBodyProps {
  loading: boolean;
  reloading: boolean;
  onReloadFrame: () => void;
  cameraError: string | null;
}

/** Single-viewport zoning workspace: top toolbar slot (toggle group / drawing
 *  status / edit toolbar — mutually exclusive), canvas filling remaining
 *  space, resizable right sidebar. The whole tree is overflow-hidden so only
 *  the sidebar's inner list scrolls. */
function ZoningTabBody({ loading, reloading, onReloadFrame, cameraError }: ZoningTabBodyProps) {
  const { t } = useT();

  return (
    <ToolbarSlotProvider>
      <div className="flex flex-1 min-h-0 flex-col gap-3">
        <ZoningHint />
        {/* Toolbar slot — FIXED height so the canvas never reflows when the
            slot's content swaps between PolygonToolbar / DrawingStatusBlock
            / EditToolbar (their natural heights differ by a few px). */}
        <div className="rounded-[var(--radius-lg)] border border-border bg-card p-2.5">
          <div className="flex h-9 items-center gap-2 md:gap-3">
            {/* Left toolbar controls. On narrow they keep their intrinsic size
                (shrink-0) so the camera selector can never ride over them; on
                desktop the slot grows (md:flex-1) so the type-toggle groups +
                ml-auto helpers spread across the row as before. min-w-0 keeps
                it shrinkable when the edit/drawing toolbars fill the slot. */}
            <div className="flex h-9 min-w-0 shrink-0 items-center md:flex-1">
              <ZoningToolbarSlot onReloadFrame={onReloadFrame} reloading={reloading} />
            </div>
            {/* Camera selector. On narrow it absorbs the remaining space and
                truncates internally (flex-1 + min-w-0) so it fills the gap
                without overlapping. On desktop it sizes to its content. */}
            <div className="flex min-w-0 flex-1 justify-end md:flex-none">
              <CameraSelector />
            </div>
          </div>
        </div>

        {cameraError ? (
          <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200">
            {cameraError}
          </div>
        ) : (
          <div className="relative flex flex-1 min-h-0 flex-row items-stretch overflow-hidden rounded-[var(--radius-lg)] border border-border bg-card">
            <div className="flex flex-1 min-w-0 min-h-0 flex-col">
              {loading ? (
                <div className="flex flex-1 items-center justify-center">
                  <Loader2 className="size-8 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <ErrorBoundary fallbackMessage={t('error.canvasRender')}>
                  <CanvasRenderer />
                </ErrorBoundary>
              )}
            </div>
            <ResizableSidebar>
              <PolygonSidebar />
            </ResizableSidebar>
          </div>
        )}
      </div>
    </ToolbarSlotProvider>
  );
}

/** Подсказка по диплинку из настройки прихода (Алексей 25.10 #2321): `?hint=arrival` —
 *  какую зону добавить и зачем другие типы; крестик убирает её из адреса. */
function ZoningHint() {
  const { t } = useT();
  const [params, setParams] = useSearchParams();
  if (params.get('hint') !== 'arrival') return null;
  const zone = params.get('zone');
  const close = () => { const next = new URLSearchParams(params); next.delete('hint'); setParams(next, { replace: true }); };
  return (
    <div className="flex items-start gap-3 rounded-[var(--radius-lg)] border border-primary/30 bg-primary/5 px-4 py-3">
      <Info className="mt-0.5 size-4 shrink-0 text-primary" />
      <div className="min-w-0 flex-1 space-y-1">
        <div className="text-sm font-medium">{t('zoning.hint.arrival.title')}</div>
        <p className="text-xs leading-relaxed text-muted-foreground">{t('zoning.hint.arrival.body')}</p>
        <Link to={`/management/queues-staff${zone ? `?zone=${zone}` : ''}`} className="inline-block pt-0.5 text-xs font-medium text-primary hover:underline">
          ← {t('zoning.hint.arrival.back')}
        </Link>
      </div>
      <button type="button" onClick={close} aria-label={t('common.close')} className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground">
        <X className="size-4" />
      </button>
    </div>
  );
}

/** Renders the active toolbar based on the canvas state pushed via
 *  ToolbarSlot context. Defaults to PolygonToolbar (type pickers). */
function ZoningToolbarSlot({ onReloadFrame, reloading }: { onReloadFrame: () => void; reloading: boolean }) {
  const { state } = useToolbarSlot();
  if (state?.kind === 'creation') {
    return (
      <DrawingStatusBlock
        entityType={state.entityType}
        pointCount={state.pointCount}
        awaitingRackSelection={state.awaitingRackSelection}
        rackSelected={state.rackSelected}
        onCancel={state.onCancel}
        onSave={state.onSave}
        onExit={state.onExit}
      />
    );
  }
  if (state?.kind === 'edit') {
    return (
      <EditToolbar
        activeType={state.activeType}
        saving={state.saving}
        onCancel={state.onCancel}
        onSave={state.onSave}
        onAction={state.onAction}
        isAvailable={state.isAvailable}
      />
    );
  }
  return <PolygonToolbar onReloadFrame={onReloadFrame} reloading={reloading} />;
}

export default BranchManagement;
